import { useAudio } from "@/components/AudioContext";
import { Volume2, VolumeX, Minus, Plus } from "lucide-react";

export function ListenButton({ stageIndex, audioKey }) {
  const { stageAudioPlaying, currentStageAudio, toggleStageAudio, STAGE_AUDIO, playExtraAudio, extraAudioPlaying, currentExtraAudio } = useAudio();

  // Support extra audio keys like "6b"
  if (audioKey) {
    const src = STAGE_AUDIO[audioKey];
    if (!src) return null;
    const isPlaying = extraAudioPlaying && currentExtraAudio === src;
    return (
      <button
        className={`listen-btn ${isPlaying ? "playing" : ""}`}
        onClick={(e) => { e.stopPropagation(); playExtraAudio(audioKey); }}
        data-testid={`listen-btn-extra-${audioKey}`}
      >
        <span style={{ fontSize: "0.9em" }}>{isPlaying ? "..." : ">"}</span>
        <span>{isPlaying ? "playing..." : "listen"}</span>
      </button>
    );
  }

  const audioSrc = STAGE_AUDIO[stageIndex];
  if (!audioSrc) return null;
  const isThisPlaying = stageAudioPlaying && currentStageAudio === audioSrc;

  return (
    <button
      className={`listen-btn ${isThisPlaying ? "playing" : ""}`}
      onClick={(e) => { e.stopPropagation(); toggleStageAudio(stageIndex); }}
      data-testid={`listen-btn-stage-${stageIndex}`}
    >
      <span style={{ fontSize: "0.9em" }}>{isThisPlaying ? "..." : ">"}</span>
      <span>{isThisPlaying ? "playing..." : "listen"}</span>
    </button>
  );
}

export function MusicControl() {
  const { bgPlaying, bgVolume, setBgVolume, toggleBgMusic, bgMusicStarted } = useAudio();

  const stepDown = (e) => {
    e.stopPropagation();
    setBgVolume(Math.max(0, bgVolume - 0.05));
  };
  const stepUp = (e) => {
    e.stopPropagation();
    setBgVolume(Math.min(1, bgVolume + 0.05));
  };

  const pct = Math.round(bgVolume * 100);

  return (
    <div
      data-testid="music-control"
      style={{
        position: "fixed",
        top: 16,
        right: 16,
        zIndex: 999,
        display: "flex",
        alignItems: "center",
        gap: 6,
        background: "rgba(34,37,24,0.85)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        borderRadius: 999,
        padding: "6px 10px",
        border: "1px solid rgba(184,212,208,0.1)",
        opacity: bgMusicStarted ? 1 : 0,
        transform: bgMusicStarted ? "translateY(0)" : "translateY(-8px)",
        transition: "opacity 0.5s ease, transform 0.5s ease",
        pointerEvents: bgMusicStarted ? "auto" : "none",
      }}
    >
      {/* Mute/Unmute */}
      <button
        onClick={(e) => { e.stopPropagation(); toggleBgMusic(); }}
        data-testid="music-toggle-btn"
        style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center", flexShrink: 0 }}
      >
        {bgPlaying ? (
          <Volume2 size={16} color="var(--accent-teal)" />
        ) : (
          <VolumeX size={16} color="rgba(163,163,163,0.4)" />
        )}
      </button>

      {/* Volume down button */}
      <button
        onClick={stepDown}
        data-testid="volume-down-btn"
        style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center" }}
      >
        <Minus size={14} color="rgba(184,212,208,0.5)" />
      </button>

      {/* Volume level indicator */}
      <span style={{
        fontSize: "0.65rem",
        color: "rgba(184,212,208,0.5)",
        fontFamily: "var(--font-body)",
        minWidth: 24,
        textAlign: "center",
        letterSpacing: "0.05em",
      }}>
        {pct}
      </span>

      {/* Volume up button */}
      <button
        onClick={stepUp}
        data-testid="volume-up-btn"
        style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center" }}
      >
        <Plus size={14} color="rgba(184,212,208,0.5)" />
      </button>
    </div>
  );
}
